import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Button } from './button';
import { trackPwaEvent, PWA_EVENTS, isAppInstalled } from '@/utils/pwaTracking';
import { useAuth } from '@/context/AuthContext'; // Import useAuth

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const InstallPrompt = () => {
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const { profile } = useAuth(); // Get user profile

  useEffect(() => {
    const appIsInstalled = isAppInstalled();
    
    const lastDismissed = localStorage.getItem('installPromptDismissed');
    const dismissedTime = lastDismissed ? parseInt(lastDismissed) : 0;
    const showAgainTimeout = 7 * 24 * 60 * 60 * 1000; // 7 days
    
    const shouldShowPromptBasedOnDismissal = !lastDismissed || Date.now() - dismissedTime > showAgainTimeout;

    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setInstallPrompt(e as BeforeInstallPromptEvent);
      
      // Show our prompt if app is not installed and dismissal timeout passed
      if (!appIsInstalled && shouldShowPromptBasedOnDismissal) {
        setTimeout(() => {
          setShowPrompt(true);
          trackPwaEvent(PWA_EVENTS.INSTALLATION_PROMPT_SHOWN, undefined, profile?.id);
        }, 2000);
      }
    };

    const handleAppInstalled = () => {
      setShowPrompt(false);
      setInstallPrompt(null);
      trackPwaEvent(PWA_EVENTS.INSTALLED, undefined, profile?.id);
      localStorage.setItem('appInstalled', 'true'); // Keep local flag for this browser
    };

    // Only add listeners if app not installed AND prompt hasn't been dismissed recently
    // This prevents listeners from running unnecessarily or re-prompting too soon
    if (!appIsInstalled && shouldShowPromptBasedOnDismissal) {
        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    }
    window.addEventListener('appinstalled', handleAppInstalled); // Keep this listener generally active

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [profile?.id, showPrompt]); // Add profile?.id and showPrompt to dependency array

  const handleInstallClick = async () => {
    if (!installPrompt) return;
    
    await installPrompt.prompt();
    const choiceResult = await installPrompt.userChoice;
    
    if (choiceResult.outcome === 'accepted') {
      trackPwaEvent(PWA_EVENTS.INSTALLATION_ACCEPTED, undefined, profile?.id);
      // The 'appinstalled' event will handle the PWA_EVENTS.INSTALLED tracking
    } else {
      trackPwaEvent(PWA_EVENTS.INSTALLATION_REJECTED, { reason: 'dismissed_native_prompt' }, profile?.id);
    }
    
    setInstallPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    trackPwaEvent(PWA_EVENTS.INSTALLATION_REJECTED, { reason: 'dismissed_custom_prompt' }, profile?.id);
    localStorage.setItem('installPromptDismissed', Date.now().toString());
  };

  if (!showPrompt) return null;

  return (
    <div className="fixed bottom-16 left-4 right-4 md:left-auto md:right-4 md:bottom-4 md:max-w-sm bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4 z-50 border border-gray-200 dark:border-gray-700">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <h3 className="font-medium text-gray-900 dark:text-gray-100">Installon Tregu si App</h3>
          <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
            Shto këtë aplikacion në ekranin tënd kryesor për qasje më të shpejtë.
          </p>
          <div className="mt-4 flex space-x-2">
            <Button 
              onClick={handleInstallClick}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              Installon
            </Button>
            <Button 
              variant="outline" 
              onClick={handleDismiss}
            >
              Jo tani
            </Button>
          </div>
        </div>
        <button 
          onClick={handleDismiss}
          className="ml-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        >
          <X size={18} />
          <span className="sr-only">Mbyll</span>
        </button>
      </div>
    </div>
  );
};

export default InstallPrompt;
